import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Mail, Phone, MapPin, Landmark, CalendarClock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import IntegrationHubPanel from "@/components/integration-hub/IntegrationHubPanel";

function resolveActiveProvider(cfg) {
  const preferred = String(cfg?.provider || "").toLowerCase();
  const parasutConnected = Boolean(cfg?.parasut?.connected);
  const bizimhesapConnected = Boolean(cfg?.bizimhesap?.connected);
  const benimposConnected = Boolean(cfg?.benimpos?.connected);
  if (preferred === "bizimhesap" && bizimhesapConnected) return "bizimhesap";
  if (preferred === "parasut" && parasutConnected) return "parasut";
  if (preferred === "benimpos" && benimposConnected) return "benimpos";
  if (bizimhesapConnected) return "bizimhesap";
  if (parasutConnected) return "parasut";
  if (benimposConnected) return "benimpos";
  return "";
}

function providerDisplayName(provider) {
  if (provider === "bizimhesap") return "BizimHesap";
  if (provider === "benimpos") return "BenimPOS";
  return "Paraşüt";
}

const COMPANY_MANAGEMENT_ITEMS = [
  { key: "company-update", label: "Firma Güncelleme", action: "Düzenle" },
  { key: "company-logo", label: "Firma Logosu", action: "Düzenle" },
  { key: "warehouses", label: "Depolar", action: "Listele" },
  { key: "email-notifications", label: "e-Posta Bildirimleri", action: "Düzenle" },
  { key: "user-management", label: "Kullanıcı Yönetimi", action: "Listele" },
  { key: "e-document", label: "e-Fatura & e-Arşiv Bilgileri", action: "Düzenle" },
];

function Field({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="font-medium text-muted-foreground">{label}:</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function toFriendlyFirmError(message) {
  const text = String(message || "");
  if (text.includes("429")) {
    return "Paraşüt API yoğunluğu nedeniyle firma bilgileri geçici olarak alınamadı. Lütfen birkaç saniye sonra tekrar deneyin.";
  }
  return text || "Firma bilgileri alınamadı";
}

const FIRMS_CACHE_KEY = "obihub_parasut_firms_cache_v1";
const ACCOUNTING_PROVIDER_CACHE_KEY = "obihub_accounting_provider_last";

function getCachedAccountingProvider() {
  try {
    const raw = String(sessionStorage.getItem(ACCOUNTING_PROVIDER_CACHE_KEY) || "").trim();
    return raw === "parasut" || raw === "bizimhesap" ? raw : "";
  } catch {
    return "";
  }
}

function cacheAccountingProvider(provider) {
  try {
    if (provider === "parasut" || provider === "bizimhesap") {
      sessionStorage.setItem(ACCOUNTING_PROVIDER_CACHE_KEY, provider);
    }
  } catch {
    // ignore storage errors
  }
}

function readCachedFirms() {
  try {
    const raw = sessionStorage.getItem(FIRMS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed?.firms)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCachedFirms(payload) {
  try {
    sessionStorage.setItem(FIRMS_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage errors
  }
}

export default function MyCompaniesPage() {
  const { startParasutConnect } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [parasutActionLoading, setParasutActionLoading] = useState(false);
  const [accountingProvider, setAccountingProvider] = useState("");
  const [bizimhesapStatus, setBizimhesapStatus] = useState(null);
  const [benimposStatus, setBenimposStatus] = useState(null);
  const [data, setData] = useState({ connected: false, firms: [], selected_company_id: null });
  const [openSections, setOpenSections] = useState({
    "company-update": false,
    "company-logo": false,
    warehouses: false,
    "email-notifications": false,
    "user-management": false,
    "e-document": false,
  });
  const [warehouses, setWarehouses] = useState([]);
  const [accountProfile, setAccountProfile] = useState(null);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [warehousesError, setWarehousesError] = useState("");
  const [einvoiceSource, setEinvoiceSource] = useState(null);
  const [einvoiceSourceSaving, setEinvoiceSourceSaving] = useState(false);
  const [turkcellConnected, setTurkcellConnected] = useState(false);
  const [accountingSettings, setAccountingSettings] = useState({});

  const firms = useMemo(() => (Array.isArray(data?.firms) ? data.firms : []), [data]);
  const selectedFirm = useMemo(() => firms.find((f) => f.is_selected) || firms[0] || null, [firms]);
  const accountEmail = useMemo(() => String(data?.account_email || ""), [data]);
  const isBizimhesap = accountingProvider === "bizimhesap";
  const isBenimpos = accountingProvider === "benimpos";
  const providerLabel = isBenimpos ? "BenimPOS" : (isBizimhesap ? "BizimHesap" : "Paraşüt");
  const displayFirmName = (firm) => firm?.company_title || firm?.name || "Firma";
  const connectedApps = useMemo(() => {
    const apps = [];
    if (accountingSettings?.parasut?.connected) apps.push({ value: "parasut", label: "Paraşüt", detail: accountingSettings.parasut.company_id || "Ön muhasebe" });
    if (accountingSettings?.bizimhesap?.connected) apps.push({ value: "bizimhesap", label: "BizimHesap", detail: accountingSettings.bizimhesap.firm_id || "Ön muhasebe" });
    if (accountingSettings?.benimpos?.connected) apps.push({ value: "benimpos", label: "BenimPOS", detail: accountingSettings.benimpos.branch_id || "Satış ve e-Fatura" });
    if (turkcellConnected) apps.push({ value: "turkcell-efatura", label: "Turkcell e-Fatura", detail: "e-Belge servisi" });
    return apps;
  }, [accountingSettings, turkcellConnected]);

  const fetchFirms = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const [cfg, meRes, turkcellStatus, sourceRes] = await Promise.all([
        api.get("/settings/accounting").catch(() => null),
        api.get("/auth/me").catch(() => null),
        api.get("/turkcell-efatura/status").catch(() => null),
        api.get("/settings/incoming-einvoice-source").catch(() => null),
      ]);
      setTurkcellConnected(Boolean(turkcellStatus?.connected));
      setAccountingSettings(cfg || {});
      setEinvoiceSource(sourceRes?.source || null);
      const activeProvider = resolveActiveProvider(cfg) || getCachedAccountingProvider();
      setAccountingProvider(activeProvider);
      if (activeProvider) cacheAccountingProvider(activeProvider);
      setAccountProfile(meRes || null);

      if (activeProvider === "benimpos") {
        const bp = cfg?.benimpos || {};
        setData({
          connected: true,
          account_email: String(meRes?.email || ""),
          selected_company_id: bp.branch_id || null,
          firms: [{
            id: bp.branch_id || "benimpos",
            name: "BenimPOS Mağazası",
            company_title: "BenimPOS Mağazası",
            tax_number: "",
            tax_office: "",
            email: String(meRes?.email || ""),
            phone: "",
            city: "",
            country: "Türkiye",
            address: "",
            status: "active",
            is_selected: true,
            raw_type: "benimpos",
          }],
        });
        return;
      }

      if (activeProvider === "bizimhesap") {
        const status = await api.get("/bizimhesap/status").catch(() => null);
        setBizimhesapStatus(status || null);
        const firmId = String(status?.firm_id || "").trim();
        const fallbackFirm = {
          id: firmId || "bizimhesap",
          name: "BizimHesap Firması",
          company_title: "BizimHesap Firması",
          tax_number: "",
          tax_office: "",
          email: String(meRes?.email || ""),
          phone: "",
          city: "",
          district: "",
          postal_code: "",
          address: "",
          country: "Türkiye",
          website: "",
          mersis_no: "",
          trade_registry_no: "",
          ioss_no: "",
          pva_no: "",
          voec_no: "",
          mid_no: "",
          logo_url: "",
          email_notifications_enabled: null,
          e_invoice_credit_count: null,
          e_invoice_sync_date: null,
          e_invoice_series_no: "",
          e_archive_series_no: "",
          e_invoice_activation_date: null,
          status: "active",
          is_selected: true,
          currency: "TRY",
          raw_type: "bizimhesap",
        };
        setData({
          connected: Boolean(status?.connected),
          account_email: String(meRes?.email || ""),
          selected_company_id: firmId || null,
          firms: Boolean(status?.connected) ? [fallbackFirm] : [],
        });
      } else {
        setBizimhesapStatus(null);
        const resp = await api.get("/parasut/firms");
        const normalized = resp || { connected: false, firms: [], selected_company_id: null };
        setData(normalized);
        if (normalized?.connected && Array.isArray(normalized?.firms)) {
          writeCachedFirms(normalized);
        }
      }
    } catch (err) {
      const msg = toFriendlyFirmError(err?.message);
      const cached = readCachedFirms();
      if (cached?.connected && Array.isArray(cached?.firms) && cached.firms.length > 0) {
        setData(cached);
        setError(`Geçici erişim sorunu: ${msg}`);
      } else {
        setError(msg);
      }
      if (silent) toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFirms();
  }, []);

  useEffect(() => {
    if (!openSections.warehouses || warehouses.length > 0 || warehousesLoading) return;
    const fetchWarehouses = async () => {
      setWarehousesLoading(true);
      setWarehousesError("");
      try {
        const resp = isBizimhesap ? await api.get("/bizimhesap/warehouses") : await api.get("/parasut/warehouses");
        const items = Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.warehouses)
            ? resp.warehouses
            : Array.isArray(resp?.items)
              ? resp.items
              : [];
        setWarehouses(items);
      } catch (err) {
        setWarehousesError(err?.message || "Depolar alınamadı");
      } finally {
        setWarehousesLoading(false);
      }
    };
    fetchWarehouses();
  }, [isBizimhesap, openSections.warehouses, warehouses.length, warehousesLoading]);

  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTestParasut = async () => {
    setParasutActionLoading(true);
    try {
      const result = await api.post("/parasut/test");
      if (result?.success) {
        toast.success(result.message || "Bağlantı başarılı.");
      } else {
        toast.error(result?.message || "Bağlantı testi başarısız.");
      }
    } catch (err) {
      toast.error(err?.message || "Bağlantı testi başarısız.");
    } finally {
      setParasutActionLoading(false);
    }
  };

  const handleDisconnectParasut = async () => {
    setParasutActionLoading(true);
    try {
      await api.post("/parasut/disconnect");
      toast.success("Paraşüt bağlantısı kesildi.");
      await fetchFirms({ silent: true });
    } catch (err) {
      toast.error(err?.message || "Bağlantı kesilemedi.");
    } finally {
      setParasutActionLoading(false);
    }
  };

  const handleConnectParasut = async () => {
    try {
      const authUrl = await startParasutConnect();
      window.location.href = authUrl;
    } catch (err) {
      toast.error(err?.message || "Paraşüt bağlantısı başlatılamadı.");
    }
  };

  const handleTestBizimhesap = async () => {
    setParasutActionLoading(true);
    try {
      const result = await api.post("/bizimhesap/test");
      if (result?.success) {
        toast.success(result.message || "Bağlantı başarılı.");
      } else {
        toast.error(result?.message || "Bağlantı testi başarısız.");
      }
    } catch (err) {
      toast.error(err?.message || "Bağlantı testi başarısız.");
    } finally {
      setParasutActionLoading(false);
    }
  };

  const handleDisconnectBizimhesap = async () => {
    setParasutActionLoading(true);
    try {
      await api.post("/bizimhesap/disconnect");
      toast.success("BizimHesap bağlantısı kesildi.");
      await fetchFirms({ silent: true });
    } catch (err) {
      toast.error(err?.message || "Bağlantı kesilemedi.");
    } finally {
      setParasutActionLoading(false);
    }
  };

  const handleTestBenimpos = async () => {
    setParasutActionLoading(true);
    try {
      const result = await api.post("/benimpos/test");
      result?.success ? toast.success(result.message || "Bağlantı başarılı.") : toast.error(result?.message || "Bağlantı testi başarısız.");
    } catch (err) {
      toast.error(err?.message || "Bağlantı testi başarısız.");
    } finally {
      setParasutActionLoading(false);
    }
  };

  const handleDisconnectBenimpos = async () => {
    setParasutActionLoading(true);
    try {
      await api.post("/benimpos/disconnect");
      toast.success("BenimPOS bağlantısı kesildi.");
      await fetchFirms({ silent: true });
    } catch (err) {
      toast.error(err?.message || "Bağlantı kesilemedi.");
    } finally {
      setParasutActionLoading(false);
    }
  };

  return (
    <div className="page-shell space-y-5" data-testid="my-companies-page">
      <div className="app-page-header">
        <h1 className="app-page-title">Firmalarım</h1>
        <p className="app-page-subtitle">
          {providerLabel} hesabınızdaki şirketleri görüntüleyin.
        </p>
      </div>

      {/* FAZ6: salt-okunur Entegrasyon Merkezi — özellik kapalıysa (404) hiçbir şey çizilmez, bu sayfa değişmez. */}
      <IntegrationHubPanel />

      {loading ? (
        <Card className="surface-card-soft">
          <CardContent className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            Firma bilgileri yükleniyor...
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="surface-card-soft border-destructive/20">
          <CardContent className="py-8 text-center">
            <p className="text-sm text-destructive font-medium">{error}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {providerLabel} bağlantınızı kontrol ederek tekrar deneyin.
            </p>
          </CardContent>
        </Card>
      ) : !data?.connected ? (
        <Card className="surface-card-soft border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{providerLabel} Hesabı</CardTitle>
            <CardDescription>{providerLabel} hesabınızı bağladığınızda firma bilgileriniz burada listelenecektir.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-2xl border border-border bg-card px-3 py-3.5 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 break-words">Bağlantı Durumu</p>
                  <p className="mt-1 text-xs text-muted-foreground">{providerLabel} bağlantısı henüz kurulmamış.</p>
                </div>
                <Badge className="bg-muted/60 text-foreground/80 hover:bg-muted/60">Bağlı Değil</Badge>
              </div>
              <div className="mt-3">
                <Button onClick={handleConnectParasut} className="h-10 rounded-xl bg-[#ef4444] text-white hover:opacity-95">
                  Paraşüt'e Bağlan
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : firms.length === 0 ? (
        <Card className="surface-card-soft">
          <CardContent className="py-8 text-center text-muted-foreground">
            {providerLabel} hesabında firma bulunamadı.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <Card className="surface-card-soft xl:col-span-2 border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Firma Yönetimi</CardTitle>
              <CardDescription>Bilgiler {providerLabel} hesabınızdan alınır.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {COMPANY_MANAGEMENT_ITEMS.map((item) => (
                <div key={item.key} className="rounded-2xl border border-border bg-card shadow-sm">
                  <div className="flex items-center justify-between px-4 py-3.5">
                    <p className="text-sm font-medium text-slate-800">{item.label}</p>
                    <Button
                      variant="link"
                      className="h-auto p-0 text-sky-700 text-xs"
                      onClick={() => toggleSection(item.key)}
                    >
                      {openSections[item.key] ? "Gizle" : item.action}
                    </Button>
                  </div>

                  {openSections[item.key] ? (
                    <div className="border-t border-border px-4 py-3">
                      {item.key === "company-update" ? (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">Firma Ünvanı / İsim</div>
                            <div className="text-sm font-medium text-slate-800 break-words">{displayFirmName(selectedFirm)}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">Ülke</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.country || "-"}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">Şehir</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.city || "-"}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">İlçe</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.district || "-"}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">Adres</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.address || "-"}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">Vergi No / TCKN</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.tax_number || "-"}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">Vergi Dairesi</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.tax_office || "-"}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">Firma E-posta</div>
                            <div className="text-sm font-medium text-slate-800">{accountEmail || selectedFirm?.email || "-"}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">Firma Telefon</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.phone || "-"}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">Mersis Numarası</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.mersis_no || "-"}</div>
                          </div>
                        </div>
                      ) : null}

                      {item.key === "company-logo" ? (
                        <div className="flex items-center gap-4 rounded-xl border border-border bg-muted/50 p-3">
                          <div className="h-20 w-44 overflow-hidden rounded-xl border border-border bg-card">
                            {selectedFirm?.logo_url ? (
                              <img src={selectedFirm.logo_url} alt="Firma logosu" className="h-full w-full object-contain" />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-sm font-semibold text-muted-foreground">
                                {String(selectedFirm?.name || "Firma").slice(0, 2).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">Logo bilgisi {providerLabel} hesabından okunur.</p>
                        </div>
                      ) : null}

                      {item.key === "warehouses" ? (
                        <div className="space-y-2">
                          {warehousesLoading ? <p className="text-sm text-muted-foreground">Depolar yükleniyor...</p> : null}
                          {warehousesError ? <p className="text-sm text-red-600">{warehousesError}</p> : null}
                          {!warehousesLoading && !warehousesError ? (
                            <div className="overflow-hidden rounded-xl border border-border">
                              <div className="grid grid-cols-12 bg-muted/30 px-3 py-2 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                                <div className="col-span-8">Depo Adı</div>
                                <div className="col-span-4 text-right">ID</div>
                              </div>
                              {warehouses.length === 0 ? (
                                <div className="px-3 py-3 text-sm text-muted-foreground">Depo bulunamadı.</div>
                              ) : (
                                warehouses.map((w) => (
                                  <div key={String(w.id)} className="grid grid-cols-12 border-t border-border/50 px-3 py-2.5 text-sm">
                                    <div className="col-span-8 text-foreground/80">{w.name || "-"}</div>
                                    <div className="col-span-4 text-right text-muted-foreground">{w.id || "-"}</div>
                                  </div>
                                ))
                              )}
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {item.key === "email-notifications" ? (
                        <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                          <div className="text-[11px] text-muted-foreground">Bildirim E-postası</div>
                          <div className="text-sm font-medium text-slate-800">{selectedFirm?.email || "-"}</div>
                        </div>
                      ) : null}

                      {item.key === "user-management" ? (
                        <div className="rounded-xl border border-border bg-muted/50 p-2.5 text-sm text-muted-foreground">
                          Kullanıcı detayları muhasebe API yetkisine bağlıdır. Bu tenant için kullanıcı listesi endpointi açık değilse sağlayıcının panelinden yönetilebilir.
                        </div>
                      ) : null}

                      {item.key === "e-document" ? (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">e-Fatura Kontör Adedi</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.e_invoice_credit_count ?? "-"}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">e-Fatura Kontör Senkronizasyon Tarihi</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.e_invoice_sync_date || "-"}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">e-Fatura Seri Numarası</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.e_invoice_series_no || "-"}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">e-Faturaya Geçme Tarihi</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.e_invoice_activation_date || "-"}</div>
                          </div>
                          <div className="rounded-xl border border-border bg-muted/50 p-2.5">
                            <div className="text-[11px] text-muted-foreground">e-Arşiv Seri Numarası</div>
                            <div className="text-sm font-medium text-slate-800">{selectedFirm?.e_archive_series_no || "-"}</div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))}
            </CardContent>
          </Card>

          {(() => {
            const options = connectedApps;
            if (!options.length) return null;
            const activeSource = einvoiceSource && options.find(o => o.value === einvoiceSource)
              ? einvoiceSource
              : options[0].value;
            return (
              <Card className="surface-card-soft border-border shadow-sm">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">Gelen Faturaların Ana Kaynağı</CardTitle>
                      <CardDescription className="mt-1">Uygulamalar bağlı kalır; Gelen Faturalar yalnızca burada seçtiğiniz kaynaktan okunur.</CardDescription>
                    </div>
                    <Badge variant="outline" className="shrink-0">Tek kaynak</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-1">
                  <div className="space-y-2">
                    {options.map((opt) => {
                      const isSelected = activeSource === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={einvoiceSourceSaving}
                          onClick={async () => {
                            if (activeSource === opt.value) return;
                            setEinvoiceSourceSaving(true);
                            try {
                              await api.put("/settings/incoming-einvoice-source", { source: opt.value });
                              setEinvoiceSource(opt.value);
                              toast.success(`Gelen fatura kaynağı "${opt.label}" olarak ayarlandı`);
                            } catch (e) {
                              toast.error(e?.message || "Ayar kaydedilemedi");
                            } finally {
                              setEinvoiceSourceSaving(false);
                            }
                          }}
                          className={`w-full flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                            isSelected
                              ? "border-primary/40 bg-primary/5"
                              : "border-border bg-card hover:bg-muted/40"
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className={`h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${isSelected ? "border-primary" : "border-muted-foreground/30"}`}>
                              {isSelected && <span className="h-2 w-2 rounded-full bg-primary" />}
                            </span>
                            <span>
                              <span className={`block text-sm font-medium ${isSelected ? "text-primary" : "text-foreground"}`}>{opt.label}</span>
                              <span className="block text-[11px] text-muted-foreground">{isSelected ? "Gelen faturalar bu bağlantıdan alınır" : "Bağlı; gelen fatura kaynağı değil"}</span>
                            </span>
                          </div>
                          {einvoiceSourceSaving && isSelected && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />}
                          {isSelected && !einvoiceSourceSaving && (
                            <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Aktif</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          <Card className="surface-card-soft border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Bağlı Finans Uygulamaları</CardTitle>
              <CardDescription>Birden fazla uygulama aynı anda bağlı kalabilir.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                {connectedApps.map((app) => (
                  <div key={app.value} className={`rounded-xl border px-3 py-3 ${einvoiceSource === app.value ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">{app.label}</p>
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">{app.detail}</p>
                      </div>
                      <Badge className={einvoiceSource === app.value ? "bg-primary/10 text-primary hover:bg-primary/10" : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"}>
                        {einvoiceSource === app.value ? "Fatura kaynağı" : "Bağlı"}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl border border-border bg-card px-3 py-3.5 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 break-words">{providerLabel} Hesabı</p>
                    <p className="mt-1 text-xs text-muted-foreground">Ayarlar sayfasındaki muhasebe bağlantısı</p>
                  </div>
                  <Badge
                    className={
                      data?.connected
                        ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                        : "bg-muted/60 text-foreground/80 hover:bg-muted/60"
                    }
                  >
                    {data?.connected ? "Bağlı" : "Bağlı Değil"}
                  </Badge>
                </div>

                <div className="mt-3 space-y-2">
                  <Field icon={Mail} label="Hesap E-posta" value={accountEmail || "-"} />
                  <Field icon={Landmark} label="Seçili Şirket ID" value={String(data?.selected_company_id || bizimhesapStatus?.firm_id || "-")} />
                  <Field icon={Landmark} label="Sağlayıcı" value={providerLabel} />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {data?.connected ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={isBenimpos ? handleTestBenimpos : (isBizimhesap ? handleTestBizimhesap : handleTestParasut)}
                        disabled={parasutActionLoading}
                      >
                        {parasutActionLoading ? "Test Ediliyor..." : "Bağlantıyı Test Et"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={isBenimpos ? handleDisconnectBenimpos : (isBizimhesap ? handleDisconnectBizimhesap : handleDisconnectParasut)}
                        disabled={parasutActionLoading}
                      >
                        Bağlantıyı Kes
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" onClick={handleConnectParasut} className="bg-[#ef4444] text-white hover:opacity-95">
                      Paraşüt'e Bağlan
                    </Button>
                  )}
                </div>
              </div>

              {firms.map((firm) => (
                <div
                  key={`${firm.id || firm.name}`}
                  className={`rounded-2xl border px-3 py-3.5 shadow-sm ${
                    firm.is_selected ? "border-sky-300 bg-sky-50/70" : "border-border bg-card"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 break-words">{displayFirmName(firm)}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{firm.id ? `Firma ID: ${firm.id}` : "Firma ID yok"}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge
                        className={
                          firm.status === "active"
                            ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                            : "bg-muted/60 text-foreground/80 hover:bg-muted/60"
                        }
                      >
                        {firm.status === "active" ? "Aktif" : "Pasif"}
                      </Badge>
                    </div>
                  </div>

                  <div className="mt-3 space-y-2">
                    <Field icon={Landmark} label="Vergi No" value={firm.tax_number} />
                    <Field icon={Landmark} label="Vergi Dairesi" value={firm.tax_office} />
                    <Field icon={CalendarClock} label="Lisans Bitiş Tarihi" value={accountProfile?.subscription_expires_at ? new Date(accountProfile.subscription_expires_at).toLocaleString("tr-TR") : "-"} />
                    <Field icon={Mail} label="E-posta" value={firm.email} />
                    <Field icon={Phone} label="Telefon" value={firm.phone} />
                    <Field icon={MapPin} label="Konum" value={[firm.city, firm.country].filter(Boolean).join(" / ")} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
